import { useState, useEffect } from "react";
import { Menu, X, Mail, Phone, Instagram, Youtube, Facebook } from "lucide-react";
import { COMPANY, LOGO_URL, openWhatsApp } from "@/lib/site";
import { Button } from "@/components/ui/button";

const links = [
  { href: "#home", label: "Home" },
  { href: "#about", label: "About" },
  { href: "#services", label: "Services" },
  { href: "#portfolio", label: "Portfolio" },
  { href: "#technologies", label: "Technologies" },
  { href: "#industries", label: "Industries" },
  { href: "#contact", label: "Contact" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* Top contact bar */}
      <div className="hidden lg:block bg-slate-50 border-b border-slate-100 text-xs text-slate-600" data-testid="top-contact-bar">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-2 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-2 hover:text-slate-900 transition-colors" data-testid="top-email-link">
              <Mail className="w-3.5 h-3.5" /> {COMPANY.email}
            </a>
            <a href={COMPANY.whatsappUrl} target="_blank" rel="noopener noreferrer" onClick={openWhatsApp} className="flex items-center gap-2 hover:text-slate-900 transition-colors" data-testid="top-phone-link">
              <Phone className="w-3.5 h-3.5" /> {COMPANY.whatsappNumber}
            </a>
          </div>
          <div className="flex items-center gap-4 text-slate-500">
            <span>Mon–Sat · 9:00 AM – 8:00 PM IST</span>
            <span className="text-slate-300">|</span>
            <a
              href={COMPANY.socials.facebook}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              data-testid="header-social-facebook"
              className="hover:text-brand-purple transition-colors"
            >
              <Facebook className="w-3.5 h-3.5" />
            </a>
            <a
              href={COMPANY.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              data-testid="header-social-instagram"
              className="hover:text-brand-purple transition-colors"
            >
              <Instagram className="w-3.5 h-3.5" />
            </a>
            <a
              href={COMPANY.socials.youtube}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="YouTube"
              data-testid="header-social-youtube"
              className="hover:text-brand-purple transition-colors"
            >
              <Youtube className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 transition-all ${scrolled ? "glass-card shadow-sm" : "bg-white/60 backdrop-blur-md"}`}
        data-testid="site-header"
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <a href="#home" className="flex items-center py-1" data-testid="site-logo-link">
            <img
              src={LOGO_URL}
              alt="ClomedixTech — Digital Software Service Solutions"
              className="h-16 md:h-20 w-auto object-contain"
            />
          </a>

          <ul className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <li key={l.href}>
                <a href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`} className="text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors relative group">
                  {l.label}
                  <span className="absolute left-0 -bottom-1 h-[2px] w-0 group-hover:w-full bg-brand-gradient transition-all duration-300" />
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-3">
            <a
              href="#contact"
              data-testid="nav-get-quote-btn"
              className="hidden md:inline-flex btn-gradient rounded-full px-5 py-2.5 text-sm font-semibold"
            >
              Get A Quote
            </a>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X /> : <Menu />}
            </Button>
          </div>
        </nav>

        {open && (
          <div className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-menu">
            <ul className="px-6 py-4 space-y-3">
              {links.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    className="block py-2 text-slate-700 font-medium"
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <a href="#contact" onClick={() => setOpen(false)} className="btn-gradient inline-flex rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="mobile-get-quote">
                  Get A Quote
                </a>
              </li>
              <li className="pt-3 border-t border-slate-100 mt-3">
                <div className="flex items-center gap-3">
                  <a href={COMPANY.socials.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="mobile-social-facebook" className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-700 hover:bg-brand-gradient hover:text-white transition-all">
                    <Facebook className="w-4 h-4" />
                  </a>
                  <a href={COMPANY.socials.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="mobile-social-instagram" className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-700 hover:bg-brand-gradient hover:text-white transition-all">
                    <Instagram className="w-4 h-4" />
                  </a>
                  <a href={COMPANY.socials.youtube} target="_blank" rel="noopener noreferrer" aria-label="YouTube" data-testid="mobile-social-youtube" className="w-9 h-9 rounded-full bg-slate-50 flex items-center justify-center text-slate-700 hover:bg-brand-gradient hover:text-white transition-all">
                    <Youtube className="w-4 h-4" />
                  </a>
                </div>
              </li>
            </ul>
          </div>
        )}
      </header>
    </>
  );
};
